import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api, BRAND, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Save, User } from "lucide-react";
import { PhotoUploader } from "@/components/PhotoUploader";

const POSITIONS = ["Levantador(a)", "Oposto(a)", "Central", "Ponteiro(a)", "Líbero", "Não definida"];

export default function StudentEditProfile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user?.student_id) return;
    api.get(`/students/${user.student_id}`)
      .then(r => {
        const s = r.data;
        setForm({
          first_name: s.first_name || (s.name?.split(" ")[0] || ""),
          last_name: s.last_name || (s.name?.split(" ").slice(1).join(" ") || ""),
          birth_date: s.birth_date || "",
          cpf: s.cpf || "",
          position: s.position || "",
          phone: s.phone || "",
          guardian: s.guardian || "",
          guardian_phone: s.guardian_phone || "",
          photo_url: s.photo_url || null,
        });
      })
      .catch(err => toast.error(formatApiError(err)));
  }, [user]);

  if (!form) return <div className="text-muted-foreground">Carregando...</div>;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.position) delete payload.position;
      await api.put("/me/profile", payload);
      toast.success("Perfil atualizado");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="student-edit-profile-page" className="max-w-2xl">
      <PageHeader eyebrow="Configurações" title="Editar perfil" subtitle="Atualize seus dados pessoais. Mudanças de turma e mensalidade são feitas pelo administrador." mascot={BRAND.MANGUITA_COACH} />

      <form onSubmit={submit} className="vv-card p-6 space-y-4">
        <div>
          <Label className="mb-2 block">Foto</Label>
          <PhotoUploader value={form.photo_url} onChange={(v) => setForm({ ...form, photo_url: v })} />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Nome *</Label>
            <Input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="profile-first-name" />
          </div>
          <div>
            <Label>Sobrenome</Label>
            <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="profile-last-name" />
          </div>
          <div>
            <Label>Data de nascimento</Label>
            <Input type="date" value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} data-testid="profile-birth-date" />
          </div>
          <div>
            <Label>CPF</Label>
            <Input value={form.cpf} onChange={(e) => setForm({ ...form, cpf: e.target.value })} placeholder="000.000.000-00" data-testid="profile-cpf" />
          </div>
          <div>
            <Label>Posição</Label>
            <Select value={form.position || "__none__"} onValueChange={(v) => setForm({ ...form, position: v === "__none__" ? "" : v })}>
              <SelectTrigger data-testid="profile-position"><SelectValue placeholder="Selecione..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">—</SelectItem>
                {POSITIONS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Telefone</Label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="profile-phone" />
          </div>
          <div>
            <Label>Responsável</Label>
            <Input value={form.guardian} onChange={(e) => setForm({ ...form, guardian: e.target.value })} data-testid="profile-guardian" />
          </div>
          <div>
            <Label>Telefone do responsável</Label>
            <Input value={form.guardian_phone} onChange={(e) => setForm({ ...form, guardian_phone: e.target.value })} data-testid="profile-guardian-phone" />
          </div>
        </div>

        <Button type="submit" disabled={saving} data-testid="profile-save-button" className="bg-[hsl(var(--vv-lime))] text-[hsl(218_50%_10%)] font-bold uppercase tracking-wider">
          <Save className="size-4 mr-2" /> {saving ? "Salvando..." : "Salvar alterações"}
        </Button>
      </form>
    </div>
  );
}
